import { useQuery } from "@rocicorp/zero/react";
import { styled } from "goober";
import { type ReactNode, useEffect, useState } from "react";
import { queries } from "../../../shared/queries";
import { riseInAnimation } from "../../hooks/useNavTransition";
import { onboardingRole } from "../../hooks/useOnboarding";
import { playChatDone, playChatNeedsInput } from "../../lib/sounds";
import { DateBadge, DateMarker, Message } from "../Chat/transcriptStyles";
import { TypewriterText } from "../Chat/TypewriterText";
import { PulsingIcon } from "../PulsingIcon";
import { ShimmerSpan } from "../ShimmerSpan";
import { Text } from "../Text";
import { WavyLine } from "../WavyLine";
import { ChatSuggestions } from "../PersonalChats/ChatSuggestions";
import { greetingName } from "../PersonalChats/NewChatEmptyState";
import {
  departmentIntro,
  pickOnboardingPrompts,
  type SignupRole,
} from "./onboardingPrompts";

export const ONBOARDING_PLACEHOLDER = "Describe what you’d like to build";

const WELCOME_DELAY_MS = 1200;
const STARTING_POINTS_DELAY_MS = 900;

const Overlay = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 24px 56px 12px;
  overflow-y: auto;
`;

const Appear = styled("div")`
  display: flex;
  flex-direction: column;
  animation: ${riseInAnimation};
`;

const Thinking = styled(Appear)`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  height: 24px;
`;

// Typing your own prompt tucks the ideas away, as the chat empty state does.
const IdeasFade = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  transition:
    opacity var(--motion-fast) var(--ease-out),
    transform var(--motion-fast) var(--ease-out);
  &[data-visible="false"] {
    opacity: 0;
    transform: translateY(-4px);
    pointer-events: none;
  }
`;

const StartingPointsPanel = styled(Appear)`
  align-items: center;
  gap: 12px;
  animation-delay: ${STARTING_POINTS_DELAY_MS}ms;
`;

function AssistantReply({ text }: { text: string }) {
  // Starting empty lets the reveal stream the reply in rather than show it at once.
  const [shown, setShown] = useState("");
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(text));
    return () => cancelAnimationFrame(frame);
  }, [text]);
  return (
    <Message style={{ flexDirection: "column", marginBottom: 32 }}>
      <TypewriterText text={shown} streaming={false} />
    </Message>
  );
}

function DelayedReply({
  text,
  delayMs,
  children,
}: {
  text: string;
  delayMs: number;
  children?: ReactNode;
}) {
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setArrived(true);
      playChatDone();
    }, delayMs);
    return () => clearTimeout(timeout);
  }, [delayMs]);
  if (!arrived) {
    return (
      <Thinking>
        <PulsingIcon data-pending>
          <WavyLine size={12} color="var(--text-secondary)" opacity={1} />
        </PulsingIcon>
        <Text variant="text-small" color="var(--text-secondary)">
          <ShimmerSpan data-pending>Thinking…</ShimmerSpan>
        </Text>
      </Thinking>
    );
  }
  return (
    <>
      <AssistantReply text={text} />
      {children}
    </>
  );
}

function StartingPoints({
  role,
  workflowId,
  visible,
  onPick,
}: {
  role: SignupRole | null;
  workflowId: string;
  visible: boolean;
  onPick: (prompt: string) => void;
}) {
  useEffect(() => {
    const timeout = setTimeout(playChatNeedsInput, STARTING_POINTS_DELAY_MS);
    return () => clearTimeout(timeout);
  }, []);
  return (
    <StartingPointsPanel>
      <IdeasFade data-visible={visible} inert={!visible}>
        <ChatSuggestions
          seed={workflowId}
          onPick={onPick}
          pickExamples={(connectorIdentifiers, count) =>
            pickOnboardingPrompts(role, connectorIdentifiers, count, workflowId)
          }
        />
      </IdeasFade>
    </StartingPointsPanel>
  );
}

export function OnboardingWelcome({
  workflowId,
  ideasVisible,
  onPick,
}: {
  workflowId: string;
  ideasVisible: boolean;
  onPick: (prompt: string) => void;
}) {
  const [currentUser] = useQuery(queries.currentUser());
  const userName = greetingName(currentUser?.name ?? "");
  const [role] = useState(onboardingRole);
  const intro = departmentIntro(role);
  const greeting = `Hi ${userName || "there"}, let’s build your first workflow. Tell me what you’d like to automate and I’ll take it from there.`;
  const ideas = intro
    ? `Since you work in ${intro.team}, I can help you ${intro.focus}. Here are a few ideas to get you started:`
    : "Here are a few ideas to get you started:";
  return (
    <Overlay>
      <DateMarker>
        <DateBadge>
          <Text variant="text-small" color="var(--text-secondary)">
            Today
          </Text>
        </DateBadge>
      </DateMarker>
      <DelayedReply delayMs={WELCOME_DELAY_MS} text={`${greeting}\n\n${ideas}`}>
        <StartingPoints
          role={role}
          workflowId={workflowId}
          visible={ideasVisible}
          onPick={onPick}
        />
      </DelayedReply>
    </Overlay>
  );
}
